// A SNAP SCROLLER, as a gesture — the mechanism the nav stack's back-swipe, the section pager and the bottom
// sheet all are.
//
// Each parks on a resting slot and lets a drag toward another one BE the gesture, so the drag and its momentum
// settle run on the compositor with the browser's own fling and snap physics. Nothing here drives motion; it
// reports position back, which is the one thing the compositor and the main thread agree on.
//
// ONE IMPLEMENTATION, because the settle DETECTION is the part that is easy to get subtly wrong: `scrollend` is
// the correct signal (it fires exactly when momentum and snap have finished) but is not everywhere, so there is
// a debounce fallback, and both paths have to agree about a held finger. A surface with its own copy of this
// silently keeps whatever the copy was written against.
//
// PART OF THE ENGINE'S PORT SET. `route-transition/surface/scroll-transition-outlet.tsx` is kept byte-identical
// with the sibling repo and imports this, so an engine sync has to carry this file across too. It lives in
// `lib/` rather than in `route-transition/core/` because the bottom sheet uses it as well, and `core/` is the
// route transition's own.

/**
 * How far a snap scroller has been dragged off its resting slot: `0` (at rest) → `1` (fully away).
 * Axis-agnostic — the stack passes `scrollLeft`, the sheet `scrollTop`.
 */
export function snapProgress(scrolled: number, maxScroll: number): number {
  if (maxScroll <= 0) return 0
  const progress = 1 - scrolled / maxScroll
  return progress < 0 ? 0 : progress > 1 ? 1 : progress
}

/**
 * How far onto the empty slot a settle must land to count as "gone" — a pop for the stack, a dismiss for the
 * sheet. Mandatory snap only ever comes to rest ON a slot, so this is not a threshold for the user's decision
 * (the browser already made that with its own fling physics); it absorbs a momentum tail that is still
 * creeping when the settle is detected.
 */
export const SNAP_LANDED = 0.98

/** How long after the last scroll event a sequence counts as settled, where `scrollend` isn't implemented. */
export const SNAP_SETTLE_DEBOUNCE_MS = 140

/**
 * Backstop for a consumer that keeps answering "not arrived yet" (see `onSettle`'s return). A scroller that
 * never reaches a slot would otherwise re-arm the debounce forever.
 */
const SNAP_RETRY_LIMIT = 20

export interface SnapScrollerHandlers {
  /**
   * Is this scroll the GESTURE's, or one we caused ourselves? Omit and every scroll counts.
   *
   * Positions a surface causes itself are not gestures: arming parks on the resting slot, and the browser then
   * moves the scroller too (a mandatory-snap re-snap when a slot gains width, a clamp back to 0 when it loses
   * it). Each fires `scroll` exactly like a drag does. Tell them apart by POSITION, never by a timer.
   */
  owns?: () => boolean
  /** The first frame of a scroll the gesture owns — where the surface switches into its dragging state. */
  onStart?: () => void
  /** Every rAF while it moves. Keep this to READS plus cheap writes; it is the frame path. */
  onFrame: () => void
  /**
   * The browser's momentum and snap have finished. `wasScrolling` is false when nothing was ever owned — a
   * `scrollend` for a position we caused.
   *
   * Return `false` to say the scroller has NOT actually arrived where it is going, and the settle will be
   * asked again after another quiet period. Settled means POSITION, not silence: on the debounce path a long
   * task freezes a snap animation and its scroll events together, so silence can mean the opposite of settled.
   * Anything else (including `void`) is taken as arrived.
   */
  onSettle: (wasScrolling: boolean) => boolean | void
}

/** Live state of an observed scroller, for the surfaces that need to ask rather than track it themselves. */
export interface SnapScrollerHandle {
  /** Stop observing. */
  stop: () => void
  /** Is a finger down on this scroller right now? */
  held: () => boolean
  /**
   * THE CONSUMER HAS TAKEN THIS SCROLL'S LANDING ITSELF — end the sequence here, and let the announcement it is
   * still owed pass as one of ours.
   *
   * For the case where POSITION already answers what silence is about to: a scroller sitting on the slot at the
   * END of its range with no finger on it is not going anywhere, so a consumer may act on that instead of waiting
   * — and the nav stack does, because the wait is the whole of a back-swipe's felt latency (measured on an iPhone:
   * `scrollend` arriving 0-509ms after the last scroll event, on a pop the engine already knew had arrived).
   *
   * Without this the sequence stays open, and both halves of that go wrong: the announcement that eventually
   * comes reports `wasScrolling` for a landing already spent, and the NEXT drag gets no `onStart` because the
   * flag it would flip was never cleared. After it, the late announcement arrives as "a position we caused"
   * (`onSettle(false)`, which every consumer already ignores) and a new drag is an ordinary fresh gesture.
   */
  consume: () => void
}

/**
 * Watch `el` and report the gesture back. Coalesced to one `onFrame` per animation frame, so a starved frame
 * lags a derived cue rather than stalling the gesture — the gesture is the browser's scroll, not ours.
 */
export function observeSnapScroller(el: HTMLElement, handlers: SnapScrollerHandlers): SnapScrollerHandle {
  let frame = 0
  let settleTimer = 0
  let scrolling = false
  let retries = 0
  /**
   * A FINGER IS DOWN — and while one is, NOTHING here settles. A mid-gesture pause is not a settle, and both
   * signals will claim it is one: the debounce fires 140ms after the last scroll event, and a drag stalled
   * under a stationary finger produces none; `scrollend` is specified to wait for the pointer, but a drag whose
   * finger has left the scroller's box can end the scrolling operation with the touch still down.
   *
   * TOUCH events, not pointer ones: the browser fires `pointercancel` the instant a native scroll claims the
   * gesture, while touch keeps firing for the gesture's whole life. The ends are watched on `window` so a
   * release outside the scroller still lands.
   */
  let held = false
  /** A settle that arrived while the finger was down. Owed to the release, which re-arms the debounce for it. */
  let owed = false
  /**
   * SNAP IS OFF for the rest of this touch. A settle arriving under a held finger is the browser announcing
   * what it does next: having decided the scrolling operation is over, a MANDATORY-snap scroller may not rest
   * between slots, so it starts its own snap animation back to the nearest one — under the finger, which then
   * yanks it back, and the surface oscillates. So the refused settle is the trigger: from that moment the
   * finger owns the position, spelled `scroll-snap-type: none` until the touch ends.
   *
   * RESTORING IT ON RELEASE IS ITSELF THE SNAP-BACK: restyling a snap scroller makes the browser re-evaluate
   * and animate to the nearest slot, which after a hold (release velocity ~0) is precisely the settle the
   * gesture wants. The settle that follows it then decides as usual.
   */
  let snapSuppressed = false

  const suppressSnap = () => {
    if (snapSuppressed) return
    snapSuppressed = true
    el.style.scrollSnapType = 'none'
  }

  const restoreSnap = () => {
    if (!snapSuppressed) return
    snapSuppressed = false
    el.style.scrollSnapType = ''
  }
  // The debounce is the fallback for engines without `scrollend` — and, on engines with it, the backstop for a
  // settle refused under a held finger and for a consumer that asked to be re-checked.
  const supportsScrollEnd = 'onscrollend' in window

  const publish = () => {
    frame = 0
    handlers.onFrame()
  }

  const armDebounce = () => {
    clearTimeout(settleTimer)
    settleTimer = window.setTimeout(settle, SNAP_SETTLE_DEBOUNCE_MS)
  }

  function settle() {
    clearTimeout(settleTimer)
    settleTimer = 0
    if (held) {
      owed = true
      suppressSnap()
      return
    }
    owed = false
    const was = scrolling
    scrolling = false
    if (handlers.onSettle(was) === false && was && ++retries < SNAP_RETRY_LIMIT) {
      // Not arrived: stay in the scrolling state and ask again after another quiet period. `scrollend` will not
      // fire a second time for a scroll that has already ended, so this path is the debounce's alone.
      scrolling = true
      armDebounce()
      return
    }
    retries = 0
  }

  const onScroll = () => {
    if (handlers.owns && !handlers.owns()) return
    if (!scrolling) {
      scrolling = true
      retries = 0
      handlers.onStart?.()
    }
    if (!frame) frame = requestAnimationFrame(publish)
    // Re-armed on every frame while it is the deciding signal, so it can only fire once the scroller has
    // stopped moving — never mid-snap. `owed` puts it back in charge on a `scrollend` engine, for the settle
    // refused under the finger that will get no second `scrollend` if the release produces no movement.
    if (!supportsScrollEnd || owed) armDebounce()
  }

  const onTouchStart = () => {
    held = true
    owed = false
    // A fresh gesture supersedes whatever the last one was still counting down to.
    clearTimeout(settleTimer)
    settleTimer = 0
  }

  const onTouchEnd = (event: TouchEvent) => {
    if (event.touches.length > 0) return // another finger is still down; the gesture isn't over
    if (!held) return
    held = false
    // Hand the snap back BEFORE the debounce is armed: the restyle starts the browser's own snap-back, whose
    // scroll events then keep re-arming that clock until it lands.
    restoreSnap()
    // The release is where the browser's momentum and snap BEGIN, so a timer armed here would read a
    // half-finished position. Only a settle we OWE gets one, because that is the case where the engine has
    // already spent its `scrollend` under the finger. Everything else the browser announces itself.
    if (owed) armDebounce()
  }

  el.addEventListener('scroll', onScroll, { passive: true })
  el.addEventListener('touchstart', onTouchStart, { passive: true })
  window.addEventListener('touchend', onTouchEnd, { passive: true })
  window.addEventListener('touchcancel', onTouchEnd, { passive: true })
  if (supportsScrollEnd) el.addEventListener('scrollend', settle)

  return {
    held: () => held,
    consume: () => {
      clearTimeout(settleTimer)
      settleTimer = 0
      owed = false
      scrolling = false
      retries = 0
    },
    stop: () => {
      if (frame) cancelAnimationFrame(frame)
      clearTimeout(settleTimer)
      restoreSnap()
      el.removeEventListener('scroll', onScroll)
      el.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('touchcancel', onTouchEnd)
      if (supportsScrollEnd) el.removeEventListener('scrollend', settle)
    },
  }
}
